import { useSyncExternalStore } from "react";

/**
 * Care-setting-aware wording for the noun Neo uses to describe an organization
 * unit. SNF clinics say "facility"; every other care setting says "branch".
 *
 * Only user-facing copy inside the Neo route uses this. Identifiers, Redux
 * paths, URL params, analytics event names and `source_type` wire values keep
 * the word "clinic" because the backend and the rest of cip-app still use it.
 *
 * Deliberately free of API/controller imports so any Neo component can use the
 * wording without dragging the app's network stack into its test environment.
 * The fetch that populates the store lives in `useNeoCareSetting.ts`.
 */

export interface ClinicWordOptions {
  capitalized?: boolean;
  plural?: boolean;
}

const SNF_WORDS = { singular: "facility", plural: "facilities" } as const;
const DEFAULT_WORDS = { singular: "branch", plural: "branches" } as const;

export interface CareSettingResolution {
  /** Which clinic ``setting`` describes, so a consumer can tell an answer about
   *  its own clinic from one left over from the clinic before it. */
  clinicId: number | null;
  /**
   * Null means no setting: the route 404s on an older backend, a network error,
   * or the backend reported "unknown". Wording falls back to the non-SNF
   * default, so an unresolved setting reads as "branch".
   */
  setting: string | null;
  /** Whether an answer for ``clinicId`` has actually landed. ``setting: null``
   *  cannot say so on its own — it is both "still asking" and "there is no
   *  setting" — and the two want opposite treatment on screen: a spinner
   *  versus showing content for every care setting. */
  resolved: boolean;
}

let resolution: CareSettingResolution = {
  clinicId: null,
  setting: null,
  resolved: false,
};
const listeners = new Set<() => void>();

export const getNeoCareSettingResolution = (): CareSettingResolution =>
  resolution;

export const getNeoCareSetting = (): string | null => resolution.setting;

export const setNeoCareSettingResolution = (
  next: CareSettingResolution
): void => {
  const trimmed = typeof next.setting === "string" ? next.setting.trim() : "";
  const setting = trimmed && trimmed !== "unknown" ? trimmed : null;
  if (
    resolution.setting === setting &&
    resolution.clinicId === next.clinicId &&
    resolution.resolved === next.resolved
  ) {
    return;
  }
  // Replaced, never mutated: ``useSyncExternalStore`` compares snapshots by
  // identity, so an in-place edit would go unnoticed and a fresh object on
  // every read would loop forever.
  resolution = { clinicId: next.clinicId, setting, resolved: next.resolved };
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const getTheWordClinic = ({
  capitalized = false,
  plural = false,
}: ClinicWordOptions = {}): string => {
  const words = resolution.setting === "snf" ? SNF_WORDS : DEFAULT_WORDS;
  const word = plural ? words.plural : words.singular;
  return capitalized ? `${word[0].toUpperCase()}${word.slice(1)}` : word;
};

/**
 * Subscribes the calling component to care-setting changes so copy built with
 * `getTheWordClinic` re-renders once the setting resolves. Returns the current
 * setting for callers that also need to branch on it.
 */
export const useNeoClinicWord = (): string | null =>
  useSyncExternalStore(subscribe, getNeoCareSetting, getNeoCareSetting);

/**
 * The full resolution — the setting plus whether it is known yet — for callers
 * that must hold a spinner rather than draw content scoped to the wrong
 * setting. `useNeoCareSetting` owns the request that fills this in.
 */
export const useNeoCareSettingResolution = (): CareSettingResolution =>
  useSyncExternalStore(
    subscribe,
    getNeoCareSettingResolution,
    getNeoCareSettingResolution
  );
